import { LOTS, SPOTS, type Vec2 } from '../world/layout';
import type { IconToken } from '../icons/index';

/**
 * Favors: the quickest money in the park. A neighbor waves you over, you do the thing,
 * they slip you a few bucks. Every one of them takes about a minute and happens close to
 * where they are.
 *
 * - beer: hand them a cold one.
 * - find: they lost something around their place; it sparkles once you're close.
 * - carry: haul something heavy a short way.
 * - fix: smack, kick or jiggle something until it works.
 * - errand: run something across the park to somebody else.
 */
export type FavorKind = 'beer' | 'find' | 'carry' | 'fix' | 'errand';

export interface FavorDef {
  id: string;
  /** Who's asking (an NPC id). */
  from: string;
  kind: FavorKind;
  /** What they say when they ask. */
  ask: string;
  /** What they say when it's done. */
  thanks: string;
  pay: readonly [number, number];
  /** The thing: found, carried or delivered. */
  item?: { name: string; icon: IconToken };
  /** carry: where it goes. fix: where you stand to do it. */
  at?: Vec2;
  /** carry: where it starts (default: by whoever asked). */
  from_at?: Vec2;
  /** fix: what you do there, as a prompt ("Smack the AC unit"). */
  verb?: string;
  /** errand: who it goes to, and what they say (you get paid right there, it was arranged). */
  to?: string;
  toLine?: string;
  /** Short name of the place it goes, for the objective ("the fire pit"). */
  place?: string;
}

export const FAVORS: readonly FavorDef[] = [
  // --- Darlene --------------------------------------------------------------------------
  {
    id: 'darlene_glasses',
    from: 'darlene',
    kind: 'find',
    ask: "'Peanut buried my reading glasses again. Somewhere 'round the office. I can't read the eviction notices without 'em, and those don't write themselves. Well. They're a template.'",
    thanks:
      "'There they are. Little dirt on 'em. Peanut, you're a menace.' She slips you a few bucks out of the petty cash.",
    pay: [6, 9],
    item: { name: 'reading glasses', icon: ':glasses:' },
  },
  {
    id: 'darlene_chair',
    from: 'darlene',
    kind: 'carry',
    ask: "'That busted office chair's gotta go in the dumpster. My back's got a reputation to protect.'",
    thanks: "'Lord, it's been squeakin' since 2004. Here, sugar.'",
    pay: [6, 9],
    item: { name: 'busted office chair', icon: ':chair:' },
    at: SPOTS.dumpster,
    place: 'the dumpster',
  },
  {
    id: 'darlene_letter',
    from: 'darlene',
    kind: 'errand',
    ask: "'Run this to Cousin Dale for me. It's a strongly worded letter. It is NOT an eviction notice. Yet.'",
    thanks: "'He read it? Good. Here's a little somethin'.'",
    pay: [5, 8],
    item: { name: 'strongly worded letter', icon: ':envelope:' },
    to: 'dale',
    toLine:
      "Dale reads it, folds it into a paper airplane and throws it at the sky. 'Tell her I said she's a beautiful woman.'",
  },
  {
    id: 'darlene_beer',
    from: 'darlene',
    kind: 'beer',
    ask: "'Fetch me a cold one, would ya, sugar? It's been a Monday since Tuesday.'",
    thanks: "Darlene cracks it, takes a long pull and closes her eyes. 'Bless you. Here.'",
    pay: [4, 6],
  },
  // --- Big Earl -------------------------------------------------------------------------
  {
    id: 'earl_beer',
    from: 'earl',
    kind: 'beer',
    ask: "'Spot me a cold one, would ya? Cooler's gone dry and the race is on.'",
    thanks: "Earl crushes half of it in one go. 'You're good people.' He pays you back, plus interest.",
    pay: [5, 7],
  },
  {
    id: 'earl_opener',
    from: 'earl',
    kind: 'find',
    ask: "'Lost my bottle opener. The good one. Shaped like a bass. It's around here somewhere, I was usin' it like ten beers ago.'",
    thanks:
      "'MY BASS.' Earl hugs you with one arm and opens a beer with the other. 'Here, for your trouble.'",
    pay: [6, 9],
    item: { name: 'bass-shaped bottle opener', icon: ':catfish:' },
  },
  {
    id: 'earl_cooler',
    from: 'earl',
    kind: 'carry',
    ask: "'Help me get this cooler over to the fire pit? I'd do it myself, but I'm supervisin'.'",
    thanks:
      "'Perfect. Right there. That's a fire pit cooler now.' He tips you like you just parked a Cadillac.",
    pay: [5, 8],
    item: { name: 'cooler full of ice', icon: ':ice:' },
    at: SPOTS.earlFirePit,
    place: "Earl's fire pit",
  },
  {
    id: 'earl_kick',
    from: 'earl',
    kind: 'fix',
    ask: "'Grill won't light. Give it a kick on the left side. Not too hard. Okay, real hard.'",
    thanks: "WHOOMP. The grill lights. Your eyebrows survive, mostly. 'THAT'S the spot!' Earl pays up.",
    pay: [4, 7],
    at: { x: SPOTS.earlGrill.x - 1.1, z: SPOTS.earlGrill.z + 0.4 },
    verb: "Kick Earl's grill",
  },
  {
    id: 'earl_ribs',
    from: 'earl',
    kind: 'errand',
    ask: "'Run this plate of ribs to Old Man Jenkins. Don't tell him they're from me. He thinks I'm a communist.'",
    thanks: "'He ate 'em? All of 'em? Ha!' Earl peels off a couple bills.",
    pay: [6, 10],
    item: { name: 'plate of ribs', icon: ':ribs:' },
    to: 'jenkins',
    toLine:
      "Jenkins takes the plate without a word and eats a rib in one motion. 'Tell Earl the sauce is too sweet.' He takes another.",
  },
  // --- Tammy Lynn -----------------------------------------------------------------------
  {
    id: 'tammy_oil',
    from: 'tammy',
    kind: 'find',
    ask: "'I dropped my lavender essential oil somewhere in the yard! That's eighty dollars of PRODUCT, hon!'",
    thanks:
      "'Oh thank goodness. You know, if you joined my team, you could find these for a living.' She pays you in cash and a pamphlet.",
    pay: [5, 8],
    item: { name: 'bottle of lavender oil', icon: ':oil-bottle:' },
  },
  {
    id: 'tammy_box',
    from: 'tammy',
    kind: 'carry',
    ask: "'Carry this box up onto my porch? It's essential oils. It's always essential oils.'",
    thanks: "'You're a doll. That box is my whole downline.' She tips you a five and a sample.",
    pay: [5, 7],
    item: { name: 'box of essential oils', icon: ':box:' },
    at: LOTS.tammy.stoop,
    place: "Tammy's porch",
  },
  {
    id: 'tammy_sample',
    from: 'tammy',
    kind: 'errand',
    ask: "'Take this sample to Darlene? She said no. But she'll LOVE it.'",
    thanks: "'Did she love it? She loved it. I knew it.' Tammy pays you.",
    pay: [5, 8],
    item: { name: 'essential oil sample', icon: ':oil-bottle:' },
    to: 'darlene',
    toLine:
      "Darlene sniffs it and gags. 'That woman is gonna sell me somethin' if it kills us both.' She keeps it anyway.",
  },
  {
    id: 'tammy_wifi',
    from: 'tammy',
    kind: 'fix',
    ask: "'My Wi-Fi's out again and I've got a Live at seven. Unplug the router on the porch and plug it back in? That's all the IT guy ever does.'",
    thanks: "The router blinks green. Tammy shrieks. 'You're a GENIUS.' She pays you like one. Barely.",
    pay: [4, 6],
    at: LOTS.tammy.stoop,
    verb: "Unplug Tammy's router and plug it back in",
  },
  // --- Cousin Dale ----------------------------------------------------------------------
  {
    id: 'dale_beer',
    from: 'dale',
    kind: 'beer',
    ask: "'Front me a cold one? I'll pay you back. No, for real this time, look, I got five bucks right here.'",
    thanks: 'Dale actually pays you. You both stare at the money. Neither of you knows how to process this.',
    pay: [5, 6],
  },
  {
    id: 'dale_lighter',
    from: 'dale',
    kind: 'find',
    ask: "'I dropped my lucky lighter somewhere around here. Don't ask why it's lucky. It's evidence.'",
    thanks:
      "'Aw, my baby.' He flicks it twice and pockets it. 'You never saw this.' He pays you in quarters.",
    pay: [6, 9],
    item: { name: 'lucky lighter', icon: ':fire:' },
  },
  {
    id: 'dale_bag',
    from: 'dale',
    kind: 'errand',
    ask: "'Give this to Skeeter. Don't shake it. Don't smell it. Don't look in it.'",
    thanks: "'He get it? He get it. Good. Here, don't spend it all on somethin' traceable.'",
    pay: [8, 12],
    item: { name: 'brown paper bag', icon: ':grocery-bag:' },
    to: 'skeeter',
    toLine: "Skeeter peeks in the bag and goes pale. 'Bro... that's a LOT of bottle rockets.'",
  },
  // --- Old Man Jenkins ------------------------------------------------------------------
  {
    id: 'jenkins_beer',
    from: 'jenkins',
    kind: 'beer',
    ask: "'Fetch an old man a beer. Doctor says I shouldn't. Doctor's dead.'",
    thanks:
      "Jenkins nods slowly. 'Good beer. Bad decisions. That's livin'.' He pays you out of a coin purse from 1962.",
    pay: [4, 6],
  },
  {
    id: 'jenkins_teeth',
    from: 'jenkins',
    kind: 'find',
    ask: "'Lost my teeth again. Somewhere between here and the mailbox. Bring 'em back before Pearl sees me.'",
    thanks:
      "He pops them in, wipes them on his shirt first (eventually), and grins. 'Ain't nobody seen nothin'.' He pays you.",
    pay: [7, 10],
    item: { name: 'dentures', icon: ':dentures:' },
  },
  {
    id: 'jenkins_light',
    from: 'jenkins',
    kind: 'fix',
    ask: "'Porch light's out again. Give it a smack. Don't use the switch, the switch don't work. The smack works.'",
    thanks: "The bulb flickers on. Jenkins salutes it. 'Forty years, that light. Same smack every time.'",
    pay: [4, 6],
    at: LOTS.jenkins.stoop,
    verb: "Smack Jenkins' porch light",
  },
  {
    id: 'jenkins_flowers',
    from: 'jenkins',
    kind: 'errand',
    ask: "'Take Pearl these flowers. She's on Possum Trot. Don't tell her they came off the median.'",
    thanks:
      "'She tucked one behind her ear? Ha!' Jenkins pays you and looks forty years younger for a minute.",
    pay: [8, 12],
    item: { name: 'flowers (from the median)', icon: ':flowers:' },
    to: 'meemaw',
    toLine:
      "Meemaw Pearl sniffs the flowers and tucks one behind her ear. 'That old goat. You tell him Thursday's fine.'",
  },
  // --- Skeeter --------------------------------------------------------------------------
  {
    id: 'skeeter_beer',
    from: 'skeeter',
    kind: 'beer',
    ask: "'Bro. Beer. For content.'",
    thanks: "Skeeter shotguns it on camera. 'Like and subscribe.' He pays you in crumpled ones.",
    pay: [4, 5],
  },
  {
    id: 'skeeter_phone',
    from: 'skeeter',
    kind: 'find',
    ask: "'Bro I dropped my phone doing a jump. It's still filming. Find it before the battery dies, there's like two hours of footage on there.'",
    thanks: "'NO WAY, it got the landing.' He shows you. It did not get the landing. He pays you anyway.",
    pay: [6, 9],
    item: { name: 'cracked phone', icon: ':phone:' },
  },
  {
    id: 'skeeter_plank',
    from: 'skeeter',
    kind: 'carry',
    ask: "'Help me get this plank over to the ramp, bro. I'm going BIGGER.'",
    thanks: "'Ramp's like nine feet now. Perfect. Don't tell my mom.' He pays you.",
    pay: [5, 7],
    item: { name: 'plank', icon: ':plank:' },
    at: SPOTS.skeeterRamp,
    place: "Skeeter's ramp",
  },
  {
    id: 'skeeter_ac',
    from: 'skeeter',
    kind: 'fix',
    ask: "'My AC's making the noise again, bro. You gotta hit it in the exact right spot. You'll know.'",
    thanks: "CLUNK. The AC purrs. Skeeter films it. 'Bro, you're a natural.'",
    pay: [4, 6],
    at: { x: LOTS.skeeter.trailer.x - 1.2, z: LOTS.skeeter.trailer.z + 2.9 },
    verb: "Smack Skeeter's AC unit",
  },
  // --- Meemaw Pearl ---------------------------------------------------------------------
  {
    id: 'meemaw_groceries',
    from: 'meemaw',
    kind: 'carry',
    ask: "'Haul my groceries in from the road, sugar? The bus dropped 'em at the curb and my hip's actin' up again.'",
    thanks:
      "'Bless your heart. Here.' She presses money into your hand and won't hear another word about it.",
    pay: [7, 10],
    item: { name: 'sacks of groceries', icon: ':grocery-bag:' },
    from_at: { x: -65.6, z: SPOTS.meemaw.z + 1.2 },
    at: SPOTS.meemawStoop,
    place: "Meemaw's porch",
  },
  {
    id: 'meemaw_remote',
    from: 'meemaw',
    kind: 'find',
    ask: "'My TV remote's run off again. I think the cat took it outside. Wheel of Fortune's on in ten minutes.'",
    thanks:
      "'Oh, you're a dear.' She pays you and changes it to Wheel of Fortune. You stay for one puzzle. You get it before she does. She's furious.",
    pay: [6, 9],
    item: { name: 'TV remote', icon: ':tv:' },
  },
  {
    id: 'meemaw_casserole',
    from: 'meemaw',
    kind: 'errand',
    ask: "'Run this casserole over to Big Earl, sugar. That boy don't eat a vegetable unless it's in a casserole.'",
    thanks: "'He liked it? Course he did.' Meemaw pays you.",
    pay: [8, 10],
    item: { name: 'casserole', icon: ':casserole:' },
    to: 'earl',
    toLine: "Earl lifts the foil like it's a treasure chest. 'Tell Pearl I'm a changed man.' He is not.",
  },
  // --- Lil' Ricky -----------------------------------------------------------------------
  {
    id: 'ricky_beer',
    from: 'ricky',
    kind: 'beer',
    ask: "'Grab me a cold one, champ. Hydration is ninety percent of cornhole.'",
    thanks: "'That's the stuff. Now I'm unbeatable. More unbeatable.' Ricky pays you.",
    pay: [4, 6],
  },
];

export const favorsFrom = (npc: string): readonly FavorDef[] => FAVORS.filter((f) => f.from === npc);

/** Everyone who ever asks for anything. */
export const FAVOR_GIVERS: readonly string[] = [...new Set(FAVORS.map((f) => f.from))];
